import React, { useState, useEffect } from 'react';
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import { Message, Table } from 'semantic-ui-react';
import useSWR from 'swr';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FHIRFetcher from '../../../../data/FHIRFetcher';
import type { ApiFetchError } from '../../../../services/api-client';
import type { FlatLocation } from '../../../../types/flat-fhir';
import * as FHIRUtils from '../../../../utils/FHIRUtils';
import { sortReducer } from '../../../../utils/SortReducer';
import type { ResourceEntry } from '../../../../utils/bundleNarrowing';
import { sortDirection } from '../../../../utils/sortDirection';

type VendorLocationsProps = {
  activeTab?: string | undefined;
  zipCode?: string | null | undefined;
};

function VendorLocations(props: VendorLocationsProps) {
  const authInfo = useAuthContext();
  const [tableData, setTableData] = useState<FlatLocation[]>([]);

  const [state, dispatch] = React.useReducer(sortReducer<FlatLocation>, {
    column: null,
    data: tableData,
    direction: null,
  });
  const { column, data, direction } = state;

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  };

  let key = `/Location?_count=1000`;

  if (props.zipCode && props.zipCode.length > 0) {
    key += `&address-postalcode=${props.zipCode}`;
  }

  //fetch locations data
  const {
    data: locationsData,
    error: locationsError,
    mutate,
  } = useSWR<fhir4.Bundle | undefined, ApiFetchError>(
    [key, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    options,
  );

  useEffect(() => {
    if (locationsData && locationsData.entry && locationsData.entry.length) {
      setTableData([]);
      const tempTableData: FlatLocation[] = [];
      // Cast, not `bundleEntriesOf`. That helper calls `Array.prototype.filter`, which drops
      // entries whose `resource` is absent (`0..1` in FHIR R4) or of another type, and also
      // substitutes `[]` for a missing `entry`. Upstream is a plain
      // `locationsData.entry.forEach(...)` under the same enclosing guard, so neither behaviour
      // is upstream's. The cast emits nothing.
      (locationsData.entry as ResourceEntry<'Location'>[]).forEach((fhirLocation) => {
        const location = FHIRUtils.parseFHIRlocation(fhirLocation.resource);

        setTableData((result) => {
          return [...result, location];
        });
        tempTableData.push(location);
      });
      dispatch({ type: 'CLEAR_FILTERS', data: tempTableData });
    }

  }, [locationsData]);

  // Inert: the sole call site (`home/vendors/index.tsx`) never passes `activeTab`.
  useEffect(() => {
    if (props.activeTab === 'ACTIVITY_LOG') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
      mutate(); //this will trigger SWR to re-Fetch
    }

  }, [props.activeTab]);

  return (
    <div>
      {locationsData && !locationsError && (
        <Table sortable celled padded color="olive">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                {...sortDirection(column === 'vendorName' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'vendorName' })}
              >
                Vendor
              </Table.HeaderCell>

              <Table.HeaderCell
                {...sortDirection(column === 'county' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'county' })}
              >
                County
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(column === 'state' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'state' })}
              >
                State
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(column === 'postalCode' ? direction : null)}
                onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'postalCode' })}
              >
                Zip Code
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map(({ id, vendorName, county, state, postalCode }) => (
              <Table.Row key={id}>
                <Table.Cell>{vendorName}</Table.Cell>
                <Table.Cell>{county}</Table.Cell>
                <Table.Cell>{state}</Table.Cell>
                <Table.Cell>{postalCode}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      {locationsData && !locationsData.entry && <Message warning header="No Activity Exists!!!" />}
      {locationsError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(locationsError as { message?: string }).message}</p>
        </Message>
      )}
    </div>
  );
}

export default VendorLocations;
